import { useEffect, useMemo } from "react"
import {
  graphql,
  type PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
  useSubscription,
} from "react-relay"
import type { Account } from "rujira.ui"
import { create } from "zustand"
import { useRujiraAccountProvider } from "@levana/chain/rujira"
import type RelayModernEnvironment from "relay-runtime/lib/store/RelayModernEnvironment"

import { AppError } from "@future/libs/error/AppError"
import { notification } from "@future/notification"

import type {
  RujiraQueryLoaderMainAccountFragment$data,
  RujiraQueryLoaderMainAccountFragment$key,
} from "./__generated__/RujiraQueryLoaderMainAccountFragment.graphql"
import type {
  RujiraQueryLoaderRootQuery,
  RujiraQueryLoaderRootQuery$data,
} from "./__generated__/RujiraQueryLoaderRootQuery.graphql"

const rootQuery = graphql`
  query RujiraQueryLoaderRootQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Layer1Account {
        id
        ...RujiraQueryLoaderMainAccountFragment
      }
    }
    thorchainV2 {
      ...RujiraDepositModalDepositFragment
      ...RujiraReusableQueriesInboundAddressesFragment
    }
  }
`

const mainAccountFragment = graphql`
  fragment RujiraQueryLoaderMainAccountFragment on Layer1Account
  @refetchable(queryName: "mainAccountQuery") {
    id
    address
    chain
    ...RujiraReusableQueriesBalancesFragment
  }
`

const subscription = graphql`
  subscription RujiraQueryLoaderMainAccountSubscription($id: ID!) {
    node(id: $id) {
      ... on Layer1Account {
        ...RujiraReusableQueriesBalancesFragment
      }
    }
  }
`

const RujiraQueryLoader = () => {
  const rujiraAccountProvider = useRujiraAccountProvider()
  const environment = useRelayEnvironment()
  const [preloadedQuery, loadQuery] =
    useQueryLoader<RujiraQueryLoaderRootQuery>(rootQuery)

  useEffect(() => {
    useRujiraQueryLoaderStore.setState({ environment })
  }, [environment])

  useEffect(() => {
    if (rujiraAccountProvider.accounts) {
      loadQuery({ ids: ids(rujiraAccountProvider.accounts) })
    }
  }, [loadQuery, rujiraAccountProvider.accounts])

  useEffect(() => {
    useRujiraQueryLoaderStore.setState({ preloadedQuery })
  }, [preloadedQuery])

  if (preloadedQuery) {
    return <RujiraPostloadedQueryLoader preloadedQuery={preloadedQuery} />
  }

  return null
}

interface RujiraPostloadedQueryLoaderProps {
  preloadedQuery: PreloadedQuery<RujiraQueryLoaderRootQuery>
}

const RujiraPostloadedQueryLoader = (
  props: RujiraPostloadedQueryLoaderProps,
) => {
  const { selected } = useRujiraAccountProvider()
  const query = usePreloadedQuery(rootQuery, props.preloadedQuery)

  useEffect(() => {
    useRujiraQueryLoaderStore.setState({ query })
  }, [query])

  const accountKey = useMemo(() => {
    return query.nodes.find((account) => {
      return selected && encodeAccount(selected) === account.id
    })
  }, [query, selected])

  const account = useFragment<RujiraQueryLoaderMainAccountFragment$key>(
    mainAccountFragment,
    accountKey,
  )

  useEffect(() => {
    useRujiraQueryLoaderStore.setState({ account })
  }, [account])

  if (accountKey?.id) {
    return <Subscription id={accountKey?.id} />
  }

  return null
}

const Subscription = (props: { id?: string }) => {
  const { id } = props

  const config = useMemo(
    (): Parameters<typeof useSubscription>[0] => ({
      subscription,
      variables: { id },
      onError: (error) => {
        notification.error(
          AppError.fromError(error, {
            text: "Graph subscription error",
            level: "fatal",
          }),
        )
      },
    }),
    [id],
  )

  useSubscription(config)

  return null
}

const ids = (accounts: Account[]): string[] => {
  return accounts.map(encodeAccount)
}

const encodeAccount = (account: Account): string => {
  return Buffer.from(
    `Layer1Account:${account.network.toLowerCase()}:${account.address}`,
  ).toString("base64")
}

interface UseRujiraQueryLoaderStoreProps {
  environment: RelayModernEnvironment | undefined
  preloadedQuery: PreloadedQuery<RujiraQueryLoaderRootQuery> | null | undefined
  query: RujiraQueryLoaderRootQuery$data | undefined
  account: RujiraQueryLoaderMainAccountFragment$data | null | undefined
}

export const useRujiraQueryLoaderStore = create<UseRujiraQueryLoaderStoreProps>(
  () => ({
    environment: undefined,
    preloadedQuery: undefined,
    query: undefined,
    account: undefined,
  }),
)

export default RujiraQueryLoader
